import React from 'react'
import { Form, Input, InputNumber, Select, Switch, Button, Space, Divider, Alert } from 'antd'
import {
  EVENT_OPTIONS,
  EVENT_OPTIONS_GROUPED,
  OPERATOR_OPTIONS,
  KIND_OPTIONS,
  GRP_OPTIONS,
  RECIPIENT_OPTIONS,
  DEDUP_OPTIONS,
  isEventWired,
} from './catalog'
import { MemberSelect } from './MemberSelect'

/**
 * The visual rule builder body (shared by Create + Edit). Must be rendered inside
 * an AntD <Form>. Conditions are a repeatable Form.List; the activity / notification
 * template sub-forms reveal when their Switch is on (Form.useWatch). Nested
 * name paths (e.g. ['activity_template','kind']) serialize straight to the jsonb columns.
 */
export function RuleFormFields() {
  const form = Form.useFormInstance()
  const triggerEvent = Form.useWatch('trigger_event', form) as string | undefined
  const createActivity = Form.useWatch('create_activity', form) as boolean | undefined
  const createNotification = Form.useWatch('create_notification', form) as boolean | undefined
  const recipientType = Form.useWatch(['notification_template', 'recipient', 'type'], form) as
    | string
    | undefined

  const evt = EVENT_OPTIONS.find((e) => e.value === triggerEvent)
  const fieldOptions = (evt?.fields ?? []).map((f) => ({ value: f, label: f }))
  const placeholderHint = `Placeholders: ${(evt?.placeholders ?? ['{who}', '{class_year}']).join('  ')}`

  return (
    <>
      <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Name is required' }]}>
        <Input placeholder="e.g. Big gift → thank-you" />
      </Form.Item>
      <Form.Item label="Description" name="description">
        <Input.TextArea rows={2} placeholder="What this rule does" />
      </Form.Item>
      <Space size="large">
        <Form.Item label="Enabled" name="enabled" valuePropName="checked" initialValue={true}>
          <Switch />
        </Form.Item>
        <Form.Item label="Priority" name="priority" initialValue={100} extra="Lower runs first">
          <InputNumber min={1} />
        </Form.Item>
      </Space>

      <Form.Item
        label="When this happens"
        name="trigger_event"
        rules={[{ required: true, message: 'Pick a trigger event' }]}
      >
        <Select
          placeholder="Select an event"
          options={EVENT_OPTIONS_GROUPED}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
      {triggerEvent && !isEventWired(triggerEvent) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: -8, marginBottom: 16 }}
          message="This event isn’t wired up yet"
          description="Nothing emits this event in production today, so this rule won’t fire — even when enabled. You can author it now so it’s ready the moment its emitter goes live."
        />
      )}

      <Divider orientation="left">Conditions (all must match)</Divider>
      <Form.List name="conditions">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...rest }) => (
              <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                <Form.Item {...rest} name={[name, 'field']} rules={[{ required: true }]}>
                  <Select style={{ width: 170 }} placeholder="Field" options={fieldOptions} />
                </Form.Item>
                <Form.Item {...rest} name={[name, 'op']} rules={[{ required: true }]}>
                  <Select style={{ width: 160 }} placeholder="Operator" options={OPERATOR_OPTIONS} />
                </Form.Item>
                <Form.Item {...rest} name={[name, 'value']} rules={[{ required: true }]}>
                  <Input placeholder="Value" />
                </Form.Item>
                <Button danger size="small" onClick={() => remove(name)}>
                  Remove
                </Button>
              </Space>
            ))}
            <Button onClick={() => add()} block>
              + Add condition
            </Button>
          </>
        )}
      </Form.List>

      <Divider orientation="left">Create a feed activity</Divider>
      <Form.Item label="Create activity" name="create_activity" valuePropName="checked" initialValue={false}>
        <Switch />
      </Form.Item>
      {createActivity && (
        <>
          <Form.Item label="Kind" name={['activity_template', 'kind']} rules={[{ required: true }]}>
            <Select options={KIND_OPTIONS} placeholder="Feed kind" />
          </Form.Item>
          <Form.Item
            label="Activity text"
            name={['activity_template', 'text_template']}
            rules={[{ required: true }]}
            extra={placeholderHint}
          >
            <Input placeholder="e.g. gave to the {fund}" />
          </Form.Item>
        </>
      )}

      <Divider orientation="left">Create an in-app notification</Divider>
      {/* A rule must produce SOMETHING — the DB enforces
          CHECK (create_activity OR create_notification). Validate it here so the
          editor gets immediate inline feedback instead of a silent 400 on save.
          dependencies on create_activity re-checks this when that toggle flips. */}
      <Form.Item
        label="Create notification"
        name="create_notification"
        valuePropName="checked"
        initialValue={false}
        dependencies={['create_activity']}
        rules={[
          ({ getFieldValue }) => ({
            validator(_, checked) {
              if (checked || getFieldValue('create_activity')) return Promise.resolve()
              return Promise.reject(
                new Error(
                  'A rule must do something — turn on “Create a feed activity” and/or “Create an in-app notification”.',
                ),
              )
            },
          }),
        ]}
      >
        <Switch />
      </Form.Item>
      {createNotification && (
        <>
          <Form.Item
            label="Title"
            name={['notification_template', 'title_template']}
            rules={[{ required: true }]}
            extra={placeholderHint}
          >
            <Input placeholder="e.g. Thanks, {who}!" />
          </Form.Item>
          <Form.Item
            label="Body"
            name={['notification_template', 'body_template']}
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={2} placeholder="e.g. Your gift to the {fund} was received." />
          </Form.Item>
          <Space size="large" wrap>
            <Form.Item label="Icon" name={['notification_template', 'icon']} initialValue="bell">
              <Input placeholder="lucide icon" />
            </Form.Item>
            <Form.Item label="Group" name={['notification_template', 'grp']} initialValue="Today">
              <Select style={{ width: 150 }} options={GRP_OPTIONS} />
            </Form.Item>
            <Form.Item
              label="Recipients"
              name={['notification_template', 'recipient', 'type']}
              initialValue="actor"
            >
              <Select style={{ width: 220 }} options={RECIPIENT_OPTIONS} />
            </Form.Item>
          </Space>
          {recipientType === 'class_year' && (
            <Form.Item label="Class year" name={['notification_template', 'recipient', 'classYear']}>
              <Input placeholder="e.g. 2001" />
            </Form.Item>
          )}
          {recipientType === 'members' && (
            <Form.Item
              label="Members"
              name={['notification_template', 'recipient', 'userIds']}
              rules={[{ required: true, message: 'Pick at least one member' }]}
            >
              <MemberSelect />
            </Form.Item>
          )}

          {/* rc-field-form (AntD's Form) submits ONLY registered `name` paths —
              an unregistered value from initialValues is simply dropped from
              onFinish. This UI has no visible editor for recipient.field /
              recipient.fallback (a 'recipient_field' recipient — e.g. the live
              mentorship_request.created rule's field:'to_user_id',
              fallback:'admins', or mention.created's field:'mentioned_user_ids'
              — is authored via SQL/API, not this builder) or for
              notification_template.route. Without these hidden registrations,
              opening ANY such rule here and hitting Save silently deletes those
              keys. See __tests__/ruleStudio.test.tsx for the round-trip
              regression test. */}
          <Form.Item name={['notification_template', 'recipient', 'field']} hidden>
            <Input />
          </Form.Item>
          <Form.Item name={['notification_template', 'recipient', 'fallback']} hidden>
            <Input />
          </Form.Item>
          <Form.Item name={['notification_template', 'route']} hidden>
            <Input />
          </Form.Item>
        </>
      )}

      <Divider orientation="left">Advanced</Divider>
      <Form.Item label="De-duplication" name="dedup_strategy" initialValue="per_row">
        <Select style={{ width: 240 }} options={DEDUP_OPTIONS} />
      </Form.Item>
    </>
  )
}
